<script setup lang="ts">
// The optional yes/no details at the bottom of the contact form.
//
// Three states, all meaningful. A question starts unanswered and shows a
// right-aligned Yes / No pair; answering swaps that pair for a switch set to
// the answer, so the choice reads at a glance and is one tap to flip. The ✕
// puts it back to unanswered — "didn't say" is real information about a lead
// and the visitor should be able to return to it.
//
// Questions come from shared/contracts/lead-qualifiers.v1.json; the browser
// only ever sends { id, value }.
//
// Answers are owned by the parent and changed through an event rather than
// v-model: a v-model here would have each row build its next map from a prop
// that is still a render behind, so two answers given in the same tick would
// collapse into one.
import { NSwitch } from 'naive-ui'
import type { QualifierAnswers, QualifierDef } from '@/shared/api/types'

const props = defineProps<{
    qualifiers: QualifierDef[]
    /** Answered ids only; anything missing is unanswered. */
    answers: QualifierAnswers
}>()

/** `null` = put this question back to unanswered. */
const emit = defineEmits<{ answer: [id: string, value: boolean | null] }>()

function answerOf(id: string) {
    return props.answers[id]
}
</script>

<template>
    <ul class="qualifiers">
        <li v-for="q in qualifiers" :key="q.id" class="row">
            <span class="question">{{ q.question }}</span>
            <div class="control">
                <template v-if="answerOf(q.id) === undefined">
                    <button type="button" class="yn" @click="emit('answer', q.id, true)">Yes</button>
                    <button type="button" class="yn" @click="emit('answer', q.id, false)">No</button>
                </template>
                <template v-else>
                    <NSwitch
                        :value="answerOf(q.id)"
                        :aria-label="q.question"
                        @update:value="(v: boolean) => emit('answer', q.id, v)"
                    >
                        <template #checked>Yes</template>
                        <template #unchecked>No</template>
                    </NSwitch>
                    <button
                        type="button"
                        class="clear"
                        title="Leave this unanswered"
                        aria-label="Leave this unanswered"
                        @click="emit('answer', q.id, null)"
                    >
                        ✕
                    </button>
                </template>
            </div>
        </li>
    </ul>
</template>

<style scoped lang="scss">
.qualifiers {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
}

.row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--site-space-md);
    padding: var(--site-space-sm) 0;
    border-bottom: 1px solid var(--site-border);

    &:last-child {
        border-bottom: none;
    }

    @media (max-width: 520px) {
        align-items: flex-start;
        flex-direction: column;
        gap: var(--site-space-xs);
    }
}

.question {
    font-size: 0.95rem;
    max-width: 34rem;
}

.control {
    display: flex;
    align-items: center;
    gap: var(--site-space-xs);
    flex-shrink: 0;
}

.yn {
    min-width: 3.4rem;
    padding: 0.35em 0.9em;
    border: 1px solid var(--site-border);
    border-radius: var(--site-radius-pill, 999px);
    background: var(--site-surface-raised, transparent);
    color: var(--site-text-muted);
    font-family: var(--site-font-body);
    font-size: 0.88rem;
    cursor: pointer;
    transition: border-color 0.12s ease, color 0.12s ease, background 0.12s ease;

    &:hover {
        border-color: var(--site-accent);
        color: var(--site-accent);
        background: var(--site-accent-soft);
    }
}

.clear {
    background: none;
    border: none;
    cursor: pointer;
    color: var(--site-text-muted);
    font-size: 0.85rem;
    line-height: 1;
    padding: 0.25rem;

    &:hover {
        color: var(--site-danger);
    }
}
</style>
